import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useCountdown } from "usehooks-ts";
import { LoggerTypes, useKYCAddUserLog } from "@src/api/useKYCAddUserLog";
import { useSendOtp } from "@src/api/useSendOtp";
import { saveSubmitOtp } from "@src/api/useSubmitOtp";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import { NUMBERS_ONLY_REGEX } from "@src/helpers/regex";
import useZustandStore from "@src/helpers/zustand";
import { cn } from "@src/lib/utils";

const OTP_TIMER = 60;

function Otp() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t } = useTranslation();
  const { data: globalState } = useZustandStore((state) => state);
  const [otp, setOtp] = useState("");
  const [errorText, setErrorText] = useState("");
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);

  const [otpCountdown, { startCountdown, resetCountdown }] = useCountdown({
    countStart: OTP_TIMER,
    intervalMs: 1000,
  });
  const sendOtpMutation = useSendOtp(startCountdown);

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.Otp,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    startCountdown();
  }, []);

  const { i18n } = useTranslation();
  const submitOtp = useMutation(saveSubmitOtp, {
    onMutate() {
      resetCountdown();
    },
    onSuccess() {
      startCountdown();
      navigate(
        `/${i18n.language}/identification/${globalState.identomat.sessionId}`
      );
    },
    onError({ response }) {
      setData({
        ...zData,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- we trust the api
        errorMessage: response.data.displayText || response.data.DisplayText,
      });
      navigate(`/${i18n.language}/error`);
    },
  });

  function onSubmit() {
    if (globalState?.isManual) {
      navigate(`/${i18n.language}/review-details`);
    } else {
      navigate(
        `/${i18n.language}/identification/${globalState.identomat.sessionId}`
      );
      submitOtp.mutate({
        apiKey: globalState.apiKey,
        phone: `${globalState.dialCode}${globalState.phone}`,
        otp,
      });
    }
  }

  const resendOtp = () => {
    sendOtpMutation.mutate({
      apiKey: globalState.apiKey,
      phone: `${globalState.dialCode}${globalState.phone}`,
    });
    resetCountdown();
  };

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <div>
        <div className="py-4">
          <TitleAndDesc
            desc={`${t("otp.desc")} +${
              globalState.dialCode
            } ${globalState.phone.replace(
              /(?<temp4>\d{3})(?<temp3>\d{2})(?<temp2>\d{2})(?<temp1>\d{2})/,
              "$1 $2 $3 $4"
            )}`}
            title={t("otp.title")}
          />
        </div>
        <div className="flex flex-col gap-3 max-md:mb-40">
          <div className="flex flex-col gap-4">
            <div className="max-w-[400px] md:max-w-[600px] mx-auto">
              <div className="flex gap-2">
                <OtpInput
                  containerStyle={{
                    justifyContent: "center",
                    // gap:  '32px',
                    marginTop: "32px",
                    fontSize: "40px",
                    color: "#475467",
                    fontWeight: 500,
                  }}
                  inputStyle={cn(
                    "h-16 md:h-[88px] !w-1/4 last:rounded-r first:rounded-l md:last:rounded-r-xl md:first:rounded-l-xl border border-gray-300 outline-none focus:border-moss-300",
                    errorText && "border-red-400",
                    "border-r-0 last:border-r"
                  )}
                  numInputs={4}
                  onChange={(newOtp) => {
                    if (errorText) {
                      setErrorText("");
                    }
                    if (NUMBERS_ONLY_REGEX.test(newOtp) || !newOtp) {
                      setOtp(newOtp);
                    }
                  }}
                  renderInput={(props) => (
                    <input
                      {...props}
                      placeholder="·"
                      autoComplete="one-time-code"
                    />
                  )}
                  shouldAutoFocus
                  value={otp}
                />
              </div>
              <p className="h-4 mt-1 text-xs text-red-400">{errorText}</p>
            </div>
            <div className="flex w-full items-center justify-between">
              <p className="hidden sm:block">{t("otp.resendTitle")}</p>
              <Button
                className="w-full sm:w-max text-boatAnchor bg-transparent hover:bg-transparent hover:shadow-none"
                disabled={otpCountdown > 0}
                onClick={resendOtp}
              >
                <p
                  className={cn(
                    "underline text-sm font-normal",
                    otpCountdown === 0 && "text-primary"
                  )}
                >
                  {t("otp.resend")} {otpCountdown}
                </p>
              </Button>
            </div>
          </div>
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={otp.length < 4}
                onClick={onSubmit}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
}

export default Otp;
